/* eslint-disable react-refresh/only-export-components */
import React, { useState, useEffect, createContext, useContext, useCallback } from "react";
import type { ReactElement } from "react";

/**
 * useIsOnline — shared browser-online hook + provider.
 * Practice apps wrap their root with <IsOnlineProvider> and read `isOnline`
 * anywhere via useIsOnline(). Also used by the Game Library adapter so all
 * apps show the offline-required message the same way.
 */

interface IsOnlineContextType {
  isOnline: boolean;
}

interface IsOnlineProviderProps {
  children: React.ReactNode;
}

const Context = createContext<IsOnlineContextType>({ isOnline: false });

export function useIsOnline(): IsOnlineContextType {
  return useContext(Context);
}

export function IsOnlineProvider({ children }: IsOnlineProviderProps): ReactElement {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );

  const setOnlineToTrue = useCallback(() => setIsOnline(true), []);
  const setOnlineToFalse = useCallback(() => setIsOnline(false), []);

  useEffect(() => {
    window.addEventListener("online", setOnlineToTrue);
    window.addEventListener("offline", setOnlineToFalse);
    return () => {
      window.removeEventListener("online", setOnlineToTrue);
      window.removeEventListener("offline", setOnlineToFalse);
    };
  }, [setOnlineToTrue, setOnlineToFalse]);

  return <Context.Provider value={{ isOnline }}>{children}</Context.Provider>;
}
